import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Buildings, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const LEVELS = ["TK", "SD", "SMP", "MTs", "MA", "SMA", "SMK", "Ma'had", "Pesantren", "Lainnya"];

export default function Lembaga() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", level: "SD", code: "", address: "", principal: "", phone: "", established_year: "" });

  const canManage = user?.role === "superadmin";
  const canEdit = ["superadmin", "admin"].includes(user?.role);

  const load = async () => {
    try { const { data } = await api.get("/lembaga"); setItems(data); } catch (e) { toast.error("Gagal memuat data"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", level: "SD", code: "", address: "", principal: "", phone: "", established_year: "" }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item, established_year: item.established_year || "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, established_year: form.established_year ? Number(form.established_year) : null };
      if (editing) await api.put(`/lembaga/${editing.id}`, payload);
      else await api.post("/lembaga", payload);
      toast.success(editing ? "Lembaga diperbarui" : "Lembaga ditambahkan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus lembaga ini?")) return;
    try { await api.delete(`/lembaga/${id}`); toast.success("Terhapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()) || i.level.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="lembaga-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Manajemen Lembaga</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Daftar Sekolah</h1>
          <p className="text-gray-600 text-sm mt-1">Kelola seluruh lembaga di bawah naungan yayasan.</p>
        </div>
        {canManage && (
          <Button onClick={openNew} data-testid="lembaga-add-button" className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white">
            <Plus size={16} weight="bold" className="mr-1.5" /> Tambah Lembaga
          </Button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cari nama atau jenjang..." className="pl-9" data-testid="lembaga-search" />
          </div>
          <div className="text-xs text-gray-500 font-mono">{filtered.length} lembaga</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Nama Lembaga</th>
                <th className="px-5 py-3 font-semibold">Jenjang</th>
                <th className="px-5 py-3 font-semibold">Kode</th>
                <th className="px-5 py-3 font-semibold">Kepala Sekolah</th>
                <th className="px-5 py-3 font-semibold">Kontak</th>
                <th className="px-5 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, idx) => (
                <tr key={l.id} className={`border-t border-gray-100 hover:bg-gray-50 ${idx % 2 === 0 ? "" : "bg-gray-50/40"}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-md bg-[#0B8D95]/8 text-[#0B8D95] flex items-center justify-center"><Buildings size={16} weight="duotone" /></div>
                      <div>
                        <div className="font-semibold text-gray-900">{l.name}</div>
                        <div className="text-xs text-gray-500">{l.address || "-"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3"><span className="inline-block px-2 py-0.5 rounded bg-[#CCFBF1] text-[#0B8D95] text-xs font-semibold">{l.level}</span></td>
                  <td className="px-5 py-3 font-mono text-xs">{l.code || "-"}</td>
                  <td className="px-5 py-3">{l.principal || "-"}</td>
                  <td className="px-5 py-3 font-mono text-xs">{l.phone || "-"}</td>
                  <td className="px-5 py-3 text-right">
                    {canEdit && (
                      <div className="inline-flex gap-1">
                        <button onClick={()=>openEdit(l)} className="p-2 hover:bg-gray-100 rounded-md text-gray-600" data-testid={`lembaga-edit-${l.id}`}><PencilSimple size={15} /></button>
                        {canManage && <button onClick={()=>remove(l.id)} className="p-2 hover:bg-red-50 rounded-md text-red-600" data-testid={`lembaga-delete-${l.id}`}><Trash size={15} /></button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-500">Belum ada lembaga. Klik "Tambah Lembaga" untuk mulai.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="lembaga-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Lembaga" : "Tambah Lembaga"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nama Lembaga *</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="lembaga-name-input" /></div>
            <div><Label>Jenjang *</Label>
              <Select value={form.level} onValueChange={(v)=>setForm({...form, level: v})}>
                <SelectTrigger className="bg-white" data-testid="lembaga-level-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{LEVELS.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Kode</Label><Input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value})} /></div>
            <div className="col-span-2"><Label>Alamat</Label><Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} /></div>
            <div><Label>Kepala Sekolah</Label><Input value={form.principal} onChange={(e)=>setForm({...form, principal: e.target.value})} /></div>
            <div><Label>No. Telepon</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Tahun Berdiri</Label><Input type="number" value={form.established_year} onChange={(e)=>setForm({...form, established_year: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="lembaga-save-button">{editing ? "Simpan" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
